import { getDB } from "./schema.js";
import { queueCloudWrite } from "./sync.js";
import { updateGame, getGameByIgdbId } from "./games.js";
import { updateBook, getBookByOlId } from "./books.js";
import {
	updateTvSeries,
	getByTvmazeId as getTvByTvmazeId,
	saveCatalog as saveTvCatalog,
} from "./tv.js";
import {
	updateAnime,
	getByTvmazeId as getAnimeByTvmazeId,
	saveCatalog as saveAnimeCatalog,
} from "./anime.js";
import { updateMovie, getMovieByImdbId } from "./movies.js";
import { updateComic, getComicByKey } from "./comics.js";
import { getGame } from "../api/igdb.js";
import { getBook } from "../api/books.js";
import { getTvShow, getTvEpisodes } from "../api/tv.js";
import { getAnime, getAnimeEpisodes, enrichAnime } from "../api/anime.js";
import { getMovie } from "../api/movies.js";
import {
	EXTERNAL_KEY,
	RESET,
	isOverridable,
	isOverridden,
	withOverride,
	withoutOverride,
	overriddenFields,
	relinkRecord,
} from "../utils/overrides.js";

// The read/write half of manual corrections (utils/overrides.js has the pure
// core): set or clear field overrides, and re-link an item to another source
// entry — for all five media types through one API.

const STORE = {
	videogame: "games",
	book: "books",
	tv: "tv",
	anime: "anime",
	movie: "movies",
	comic: "comics",
};
const CATALOG_STORE = { tv: "tv_catalog", anime: "anime_catalog" };
const UPDATE = {
	videogame: updateGame,
	book: updateBook,
	tv: updateTvSeries,
	anime: updateAnime,
	movie: updateMovie,
	comic: updateComic,
};
// Media types whose external id is a string (everything else is numeric).
const STRING_ID_MEDIA = new Set(["book", "movie", "comic"]);

async function getRecord(mediaType, id) {
	const store = STORE[mediaType];
	if (!store) throw new Error(`Unknown media type ${mediaType}`);
	const db = await getDB();
	const rec = await db.get(store, id);
	if (!rec) throw new Error(`${mediaType} ${id} not found`);
	return rec;
}

function sameValue(a, b) {
	return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// The tracked item (if any) already linked to this source id.
export async function findByExternalId(mediaType, externalId) {
	if (mediaType === "book") return getBookByOlId(String(externalId));
	if (mediaType === "movie") return getMovieByImdbId(String(externalId));
	if (mediaType === "comic") return getComicByKey(String(externalId));
	const n = Number(externalId);
	if (mediaType === "videogame") return getGameByIgdbId(n);
	if (mediaType === "tv") return getTvByTvmazeId(n);
	return getAnimeByTvmazeId(n);
}

// Apply a patch of { field: value | RESET }. Setting a field to exactly what the
// catalog says drops the override rather than keeping a no-op lock. Goes through
// the media type's update function (updated_at bump + cloud sync).
export async function setOverrides(mediaType, id, patch) {
	let record = await getRecord(mediaType, id);
	const update = {};
	for (const [field, value] of Object.entries(patch ?? {})) {
		if (!isOverridable(mediaType, field)) {
			throw new Error(`"${field}" can't be edited for ${mediaType}`);
		}
		const catalogValue = isOverridden(record, field)
			? record.source_values[field]
			: (record[field] ?? null);
		record =
			value === RESET || sameValue(value, catalogValue)
				? withoutOverride(record, field)
				: withOverride(record, field, value);
		update[field] = record[field] ?? null;
	}
	if (Object.keys(update).length === 0) return record;
	update.source_values = record.source_values ?? {};
	// Books used to carry a separate cover_override_url; the override layer
	// supersedes it, so any cover change here retires the legacy field.
	if (mediaType === "book" && "cover_url" in update) update.cover_override_url = null;
	return UPDATE[mediaType](id, update);
}

export function setOverride(mediaType, id, field, value) {
	return setOverrides(mediaType, id, { [field]: value });
}

export function clearOverride(mediaType, id, field) {
	return setOverrides(mediaType, id, { [field]: RESET });
}

export async function clearAllOverrides(mediaType, id) {
	const record = await getRecord(mediaType, id);
	const fields = overriddenFields(record);
	if (fields.length === 0) return record;
	return setOverrides(mediaType, id, Object.fromEntries(fields.map((f) => [f, RESET])));
}

// The source's current record for an external id (used by undo).
export async function fetchFresh(mediaType, externalId) {
	if (mediaType === "videogame") return getGame(externalId);
	if (mediaType === "book") return getBook(externalId);
	if (mediaType === "movie") return getMovie(externalId);
	if (mediaType === "tv") return getTvShow(externalId);
	return getAnime(externalId);
}

// Re-point an item at `fresh` (a search result or detail record from the same
// source). Refuses a target already tracked as another item (merging is not
// supported). Locally: one put. Cloud: rows key on external_id, so the outbox
// gets delete(old) then create(new) — same uuid, so groups/rankings/notifications
// keep pointing at it. TV/anime: the old catalog cache is dropped and the new one
// fetched right away (best effort; the detail view fetches lazily otherwise).
export async function relinkItem(mediaType, id, fresh, { taxonomy, relinkedFrom } = {}) {
	const record = await getRecord(mediaType, id);
	const key = EXTERNAL_KEY[mediaType];
	const targetExt = fresh?.[key];
	if (targetExt == null) throw new Error("That entry can't be linked (it has no id).");
	if (String(targetExt) === String(record[key])) {
		throw new Error("Already linked to that entry.");
	}
	const clash = await findByExternalId(mediaType, targetExt);
	if (clash && clash.id !== id) {
		const err = new Error(`Already in your library as "${clash.title}".`);
		err.code = "ALREADY_TRACKED";
		throw err;
	}

	const next = relinkRecord(record, mediaType, fresh, { taxonomy, relinkedFrom });
	const db = await getDB();
	await db.put(STORE[mediaType], next);
	await queueCloudWrite({ type: "delete", mediaType, record });
	await queueCloudWrite({ type: "create", mediaType, record: next });

	const catalogStore = CATALOG_STORE[mediaType];
	if (catalogStore) {
		try {
			await db.delete(catalogStore, record.tvmaze_id);
		} catch {
			// nothing cached for the old id
		}
		try {
			const fetchEpisodes = mediaType === "tv" ? getTvEpisodes : getAnimeEpisodes;
			const save = mediaType === "tv" ? saveTvCatalog : saveAnimeCatalog;
			const data = await fetchEpisodes(next.tvmaze_id);
			if (data) await save(data);
		} catch {
			// offline / worker hiccup — the detail view re-fetches on open
		}
	}
	return next;
}

// Go back to the entry the item was linked to before the last re-link. Fetches
// that entry fresh from the source (and MAL genres for anime), re-links, and
// forgets the history so the menu doesn't offer an undo-of-undo.
export async function undoRelink(mediaType, id) {
	const record = await getRecord(mediaType, id);
	const prev = record.relinked_from;
	if (!prev?.external_id) throw new Error("Nothing to undo.");
	const key = EXTERNAL_KEY[mediaType];
	const fetched = await fetchFresh(mediaType, prev.external_id);
	const fresh = {
		...fetched,
		[key]: STRING_ID_MEDIA.has(mediaType)
			? String(prev.external_id)
			: Number(prev.external_id),
	};
	let taxonomy;
	if (mediaType === "anime") {
		try {
			taxonomy = await enrichAnime(fresh.title, fresh.premiered_year);
		} catch {
			taxonomy = undefined; // keep the current genres
		}
	}
	return relinkItem(mediaType, id, fresh, { taxonomy, relinkedFrom: null });
}
